import { AppConfigContext } from '@plato/app-core/context/app-config';
import { TenantContext, type Tenant } from '@plato/app-core/context/tenant-context';
import { UserContext, type User } from '@plato/app-core/context/user-context';
import { useIsManagerView } from '@plato/app-core/hooks/use-view-mode';
import { DialogProvider } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LoadingBlockFullScreen } from '@plato/ui-legacy/components/loading/loading-block-full-screen';
import { cn } from '@plato/ui/util/classes';
import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import { posthog } from 'posthog-js';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { IntercomProvider } from '../../../components/providers/intercom-provider.tsx';
import { useImpersonation } from '../../../lib/hooks/use-impersonation.ts';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import { AskPlatoFab } from '../../ai-chat/components/ask-plato-fab.tsx';
import { AskPlatoProvider } from '../../ai-chat/components/ask-plato-provider.tsx';
import { useAskPlatoEnabled } from '../../ai-chat/hooks/use-ask-plato-enabled.ts';
import { CatalogProvider } from '../../catalog/components/global-catalog-provider.tsx';
import { AllDialogs, AllDialogsProvider } from '../../dialogs/all-dialogs.tsx';
import { LegacyLanguageUrl } from '../../i18n/components/legacy-language-url.tsx';
import { useVersionWatch } from '../../version-watch/use-version-watch.ts';
import { FullWidthNav } from './full-width-nav.tsx';
import { GlobalSearchBarProvider } from './global-search-bar-provider.tsx';
import { IconOnlyNav } from './icon-only-nav.tsx';
import { MobileBottomNav } from './mobile-bottom-nav.tsx';
import { MobileTopBar } from './mobile-top-bar.tsx';
import { ViewModePageGuard } from './view-mode-page-guard.tsx';
export { NavPathOverrideEnum } from '../contracts.ts';

export type RouteHandle = {
    disableDialogs?: boolean;
};

function PlatoUserLayoutImplementation(): ReactNode {
    useVersionWatch();
    const [smallScreenDrawOpen, setSmallScreenDrawOpen] = useState(false);
    const isManagerView = useIsManagerView();
    const askPlatoEnabled = useAskPlatoEnabled();

    // View-mode chrome policy: the rep-oriented "Create new" is hidden in
    // Manager view.
    const showCreateNew = !isManagerView;

    const [desktopSidebarOpen, setDesktopSidebarOpen] = useLocalStorage<boolean>(
        'desktop-sidebar-open',
        true,
    );

    const closeDrawer = (): void => {
        setSmallScreenDrawOpen(false);
    };

    // Close mobile drawer on orientation change
    useEffect(() => {
        const orientation = screen.orientation;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This is legacy code, if you know what are you doing, please fix me
        if (!orientation) {
            return;
        }

        const handleOrientationChange = (): void => {
            closeDrawer();
        };

        orientation.addEventListener('change', handleOrientationChange);
        return () => {
            orientation.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    return (
        <GlobalSearchBarProvider onBeforeOpenSearch={closeDrawer}>
            <div className="z-30 h-full">
                {/* Side navigation bars */}
                <div className="fixed z-30 hidden h-full w-[52px] border-r border-neutral-200 bg-neutral-50 sm:inset-y-0 sm:left-0 sm:block xl:hidden">
                    <IconOnlyNav
                        isDesktopScreenSize={false}
                        smallScreenDrawOpen={smallScreenDrawOpen}
                        setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                        setDesktopSidebarOpen={setDesktopSidebarOpen}
                        closeDrawer={closeDrawer}
                        showCreateNew={showCreateNew}
                    />
                </div>
                {/* full desktop sidebar */}
                <div
                    className={cn(
                        'z-30 hidden h-full border-r border-neutral-200 bg-neutral-50 xl:fixed xl:inset-y-0 xl:left-0 xl:block',
                        desktopSidebarOpen ? 'xl:w-[250px]' : 'xl:w-[52px]',
                    )}
                >
                    {desktopSidebarOpen ? (
                        <FullWidthNav
                            isDesktopScreenSize={true}
                            closeDrawer={closeDrawer}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                            showCreateNew={showCreateNew}
                        />
                    ) : (
                        <IconOnlyNav
                            isDesktopScreenSize={true}
                            smallScreenDrawOpen={smallScreenDrawOpen}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                            closeDrawer={closeDrawer}
                            showCreateNew={showCreateNew}
                        />
                    )}
                </div>
                {/* Mobile bottom nav outside the z-0 stacking context so backdrop-filter works */}
                <MobileBottomNav />
                {/* Desktop Ask Plato entry point; mobile uses the top-bar button */}
                {askPlatoEnabled && <AskPlatoFab />}
                <div
                    className={cn(
                        'z-0 flex h-dvh max-h-dvh flex-col pl-0 sm:pl-[52px]',
                        'transition-[padding] ease-standard duration-interactive',
                        desktopSidebarOpen ? 'xl:pl-[250px]' : 'xl:pl-[52px]',
                    )}
                >
                    {/* Mobile top bar (no hamburger — nav lives in bottom bar) */}
                    <MobileTopBar showCreateNew={showCreateNew} showAskPlato={askPlatoEnabled} />
                    <div data-slot="app-content" className="flex grow min-h-0 flex-col lg:rounded-tl-lg">
                        <Outlet />
                    </div>
                </div>
            </div>
            <AllDialogs />
        </GlobalSearchBarProvider>
    );
}

export function PlatoUserLayout(): ReactNode {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const { signOutFn } = useSignOut();
    const [impersonatedUserId] = useImpersonation();
    const isImpersonating = impersonatedUserId !== undefined;

    const {
        data: platoUser,
        error,
        isLoading,
    } = api.platoUser.me.getMe.useQuery(undefined, {
        // cache result for a short time to reduce requests
        staleTime: 60_000,
        // don't retry expected errors
        retry: (_, error) => error.data?.code !== 'FORBIDDEN' && error.data?.code !== 'NOT_FOUND',
    });

    const { data: superAdmin } = api.superAdmin.me.getMe.useQuery(undefined, {
        staleTime: 60_000 * 5,
        enabled: isImpersonating,
    });

    const { data: appConfig } = api.platoUser.query.appConfig.getAppConfig.useQuery(undefined, {
        enabled: !!platoUser,
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchInterval: 5_000 * 60, // 5 minutes
    });

    useEffect(() => {
        if (!error || !(error instanceof TRPCClientError)) {
            return;
        }
        const data: unknown = error.data;
        if (typeof data !== 'object' || data === null || !('code' in data)) {
            return;
        }
        if (data.code === 'FORBIDDEN') {
            void navigate('/admin', { replace: true });
        } else if (data.code === 'NOT_FOUND') {
            signOutFn();
        }
    }, [error, navigate, signOutFn]);

    useEffect(() => {
        if (!platoUser || process.env.NODE_ENV === 'development') {
            return;
        }

        if (isImpersonating && superAdmin) {
            Sentry.setUser({
                id: superAdmin.id,
                email: superAdmin.email,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                role: 'SUPER_ADMIN',
            });
            Sentry.setContext('Impersonation', {
                email: platoUser.email,
                id: platoUser.id,
                role: platoUser.role,
            });

            Sentry.setTags({
                impersonation: true,
            });
        } else {
            Sentry.setUser({
                id: platoUser.id,
                email: platoUser.email,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                role: platoUser.role,
            });
        }

        Sentry.setTag('tenantId', platoUser.platoCustomer.id);
        Sentry.setTag('tenantSlug', platoUser.platoCustomer.slug);
        Sentry.setTag('userRole', platoUser.role);
        Sentry.setTag('impersonation', isImpersonating);
    }, [platoUser, isImpersonating, superAdmin]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            return;
        }

        if (isImpersonating && superAdmin) {
            posthog.identify(superAdmin.email, {
                IS_SUPER_ADMIN: true,
                id: superAdmin.id,
                firstName: superAdmin.firstName,
                lastName: superAdmin.lastName,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                email: superAdmin.email,
            });
            posthog.group('plato_customer', 'id:plato_team_superuser', {
                // PostHog displays groups by their `name` property; without it the UI falls back to the raw group key
                name: 'Plato Team',
                customer_id: 'Plato Team',
                customer_name: 'Plato Team',
            });
            return;
        }

        if (!isImpersonating && platoUser) {
            posthog.identify(platoUser.email, {
                IS_SUPER_ADMIN: false,
                id: platoUser.id,
                firstName: platoUser.firstName,
                lastName: platoUser.lastName,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
                erpSalesRepId: platoUser.erpSalesRepId,
                salesRepIds: platoUser.salesRepIds,
                showDataForErpSalesRepId: platoUser.showDataForErpSalesRepId,
                email: platoUser.email,
                role: platoUser.role,
                description: platoUser.descriptionString,
            });
            posthog.group('plato_customer', `id:${platoUser.platoCustomer.id}`, {
                // PostHog displays groups by their `name` property; without it the UI falls back to the raw group key
                name: platoUser.platoCustomer.name,
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
            });
        }
    }, [platoUser, isImpersonating, superAdmin]);

    const language = platoUser?.language;

    useEffect(() => {
        if (language) {
            void i18n.changeLanguage(language);
        }
    }, [i18n, language]);

    const user = useMemo<User | undefined>(
        () =>
            platoUser
                ? {
                      id: platoUser.id,
                      firstName: platoUser.firstName,
                      lastName: platoUser.lastName,
                  }
                : undefined,
        [platoUser],
    );

    const tenant = useMemo<Tenant | undefined>(
        () =>
            platoUser
                ? {
                      id: platoUser.platoCustomer.id,
                      name: platoUser.platoCustomer.name,
                      webshopUrl: platoUser.platoCustomer.webshopUrl ?? undefined,
                  }
                : undefined,
        [platoUser],
    );

    if (isLoading || !platoUser || !user || !tenant || !appConfig) {
        return <LoadingBlockFullScreen />;
    }

    return (
        <UserContext.Provider value={user}>
            <TenantContext.Provider value={tenant}>
                <AppConfigContext.Provider value={appConfig}>
                    <IntercomProvider user={platoUser}>
                        <ViewModePageGuard>
                            <AllDialogsProvider>
                                <DialogProvider>
                                    <CatalogProvider user={platoUser}>
                                        <AskPlatoProvider>
                                            <LegacyLanguageUrl />
                                            <PlatoUserLayoutImplementation />
                                        </AskPlatoProvider>
                                    </CatalogProvider>
                                </DialogProvider>
                            </AllDialogsProvider>
                        </ViewModePageGuard>
                    </IntercomProvider>
                </AppConfigContext.Provider>
            </TenantContext.Provider>
        </UserContext.Provider>
    );
}
